import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { ThemePreference } from "@deca/shared-types";
import {
  applyThemeToDocument,
  cacheTheme,
  nextThemePreference,
  readCachedTheme,
  resolveTheme,
} from "../lib/theme";

/**
 * Tema claro/oscuro con tres estados (CLAUDE.md Fase 4).
 *
 * La preferencia es del **usuario**, no del navegador: se guarda en
 * `User.themePreference` vía API, de modo que viaja entre el panel y la app
 * móvil del mismo conductor o gestor. `localStorage` es solo una caché para
 * pintar el tema correcto antes de que responda la API — nunca la fuente de
 * verdad.
 */

interface ThemeContextValue {
  preference: ThemePreference;
  resolved: "light" | "dark";
  /** Cambia el tema: si `persist` es falso, no llama a la API (antes de login). */
  setPreference: (preference: ThemePreference, options?: { persist?: boolean }) => void;
  /** Ciclo del botón de tema: Claro → Oscuro → Sistema. */
  cycle: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export interface ThemeProviderProps {
  children: ReactNode;
  /** Valor inicial cuando ya se conoce la preferencia del usuario (tras /me). */
  initialPreference?: ThemePreference | undefined;
  /** Persiste en la API; ausente = solo cambio local (pantalla de acceso). */
  onPersist?: (preference: ThemePreference) => void;
}

function systemPrefersDark(): boolean {
  return typeof window !== "undefined" && window.matchMedia("(prefers-color-scheme: dark)").matches;
}

export function ThemeProvider({ children, initialPreference, onPersist }: ThemeProviderProps) {
  const [preference, setPreferenceState] = useState<ThemePreference>(
    () => initialPreference ?? readCachedTheme(),
  );
  const [prefersDark, setPrefersDark] = useState<boolean>(() => systemPrefersDark());

  // Sigue los cambios del sistema en vivo: si el usuario eligió "Sistema" y
  // cambia el tema del sistema operativo, el panel acompaña sin recargar.
  useEffect(() => {
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const listener = (event: MediaQueryListEvent) => setPrefersDark(event.matches);
    media.addEventListener("change", listener);
    return () => media.removeEventListener("change", listener);
  }, []);

  // Cuando llega la preferencia del servidor (o cambia de usuario), manda ella.
  useEffect(() => {
    if (initialPreference) setPreferenceState(initialPreference);
  }, [initialPreference]);

  const resolved = resolveTheme(preference, prefersDark);

  useEffect(() => {
    applyThemeToDocument(resolved);
  }, [resolved]);

  const setPreference = useCallback(
    (next: ThemePreference, options?: { persist?: boolean }) => {
      setPreferenceState(next);
      cacheTheme(next);
      if (options?.persist !== false) onPersist?.(next);
    },
    [onPersist],
  );

  const cycle = useCallback(() => {
    setPreference(nextThemePreference(preference));
  }, [preference, setPreference]);

  const value = useMemo<ThemeContextValue>(
    () => ({ preference, resolved, setPreference, cycle }),
    [preference, resolved, setPreference, cycle],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) throw new Error("useTheme se ha usado fuera de <ThemeProvider>");
  return context;
}
